import { loadVidvertoFromSnippet } from "../ads/vidverto-loader.js";
import { createLogger } from "../helpers/logger.js";

function destroyPlayer(player, dom, settings, container, passback = 'yes', debug = false) {
  const log = createLogger("PlayerLifecycle", debug);
  const snippet =
    settings?.ads?.passback ||
    container?.__cleonPassbackSnippet ||
    "";

  const shouldLoadPassback =
    passback === "yes" &&
    !!container &&
    typeof snippet === "string" &&
    !!snippet.trim();


    try {
        if (player && typeof player.dispose === "function") {
            if (player.__cleonInterval) {
                clearInterval(player.__cleonInterval);
                player.__cleonInterval = null;
            }
            player.__destroying = true;
            player.dispose();
            log.debug("🧹 Player disposed successfully");
        }
    } catch (e) {
        log.debug("⚠ Player dispose error:", e);
    }

    if (!shouldLoadPassback) {
      removeCleonVisualSpacers("player-disposed-no-passback");
      return;
    }
    log.debug("🎯 Loading passback snippet and handing layout to Vidverto");

    container.hidden = false;
    container.removeAttribute?.("hidden");
    container.classList?.remove("ad-slot--pending");

    container.style.setProperty("display", "block", "important");
    container.style.setProperty("visibility", "visible", "important");
    container.style.setProperty("opacity", "1", "important");
    container.style.setProperty("pointer-events", "auto", "important");

    loadVidvertoFromSnippet(container, snippet);
    
}

export function safeDestroy(player, dom, settings, container, passback = "no", debug = false) {
  const log = createLogger("PlayerLifecycle", debug);
  const wantsPassback = passback === "yes" || passback === true;

  if (!player) {
    if (wantsPassback) {
      destroyPlayer(null, dom, settings, container, "yes", debug);
    }
    return;
  }

  if (player.isDisposed?.() || player.__destroying) return;
  player.__destroying = true;

  if (typeof player.__cleanupSticky === "function") {
    try {
      player.__cleanupSticky();
    } catch {}
  }
  
  const prevLevel = (window.videojs?.log?.level && window.videojs.log.level()) || null;
  try { window.videojs?.log?.level?.("off"); } catch {}

  const swallowTeardownError = () => {
    try {
      const err = player.error?.();
      if (player.__destroying && err?.code === 4) {
        // clear it so video.js doesn't keep shouting
        try { player.error(null); } catch {}
      }
    } catch {}
  };
  try { player.on("error", swallowTeardownError); } catch {}

  stopPlaybackHard(player);
  if (!wantsPassback) {
    collapseIframes();
    if (container) container.hidden = true;
  }
  

  try { player.ima?.stop?.(); } catch {}
  try { player.ima?.reset?.(); } catch {}
  try { player.ima?.destroy?.(); } catch {}

  // prevent our logic from firing
  try { player.off("timeupdate"); } catch {}
  try { player.off("play"); } catch {}
  try { player.off("pause"); } catch {}
  try { player.off("adstart"); } catch {}
  try { player.off("adend"); } catch {}
  try { player.off("adserror"); } catch {}
  try { player.off("adskip"); } catch {}
  try { player.off("contentresumerequested"); } catch {}
  try { player.off("contentpauserequested"); } catch {}

  setTimeout(() => {
    destroyPlayer(
      player,
      dom,
      settings,
      container,
      wantsPassback ? "yes" : "no",
      debug
    );

    try { player.off("error", swallowTeardownError); } catch {}
    try { if (prevLevel) window.videojs.log.level(prevLevel); } catch {}
  }, 0);
}

function stopPlaybackHard(player) {
  if (!player || player.isDisposed?.()) return;

  // Stop playback ASAP
  try { player.pause?.(); } catch {}
  try { player.trigger?.("pause"); } catch {}

  // Kill streaming pipeline (VHS/HLS) if present
  try {
    const tech = player.tech?.(true);
    // Video.js VHS (videojs-http-streaming)
    if (tech?.vhs) {
      try { tech.vhs.dispose?.(); } catch {}
    }
    // Some older setups expose hls directly
    if (tech?.hls) {
      try { tech.hls.stop?.(); } catch {}
      try { tech.hls.dispose?.(); } catch {}
    }
  } catch {}

  // Clear src to stop further segment requests
  try { player.reset?.(); } catch {}
  try { player.src?.([]); } catch {}
  try { player.load?.(); } catch {}
}

function applyStyles(el, styles) {
  if (!el) return;
  Object.assign(el.style, styles);
}

export function burstIframes({ width = 640, height = 360 } = {}) {
  const host = (window.location?.hostname || "").toLowerCase();
  if (host === "adserver.cleon.tv" || host.endsWith(".adserver.cleon.tv")) return;
  if (window.top === window.self) return;

  const inner = window.frameElement;
  if (!inner || inner.nodeType !== 1) return;

  const html = document.documentElement;
  const body = document.body;

  if (!inner.hasAttribute("data-prev-style")) {
    inner.setAttribute("data-prev-style", inner.getAttribute("style") || "");
    inner.setAttribute("data-prev-w", inner.getAttribute("width") || "");
    inner.setAttribute("data-prev-h", inner.getAttribute("height") || "");
  }

  const ratio = width > 0 && height > 0 ? height / width : 400 / 640;

  inner.setAttribute("data-burst", "1");

  applyStyles(html, {
    height: "100%",
    minHeight: "",
  });

  applyStyles(body, {
    height: "100%",
    minHeight: "",
    margin: "0",
  });

  const parentWidth = inner.parentElement?.clientWidth || window.innerWidth || width;
  const parentHeight = inner.parentElement?.clientHeight || 0;

  const renderedWidth = Math.min(width, parentWidth);
  let renderedHeight = Math.round(renderedWidth * ratio);

  if (parentHeight > 120) {
    renderedHeight = Math.min(renderedHeight, parentHeight);
  }

  applyStyles(inner, {
    display: "block",
    position: "relative",
    left: "",
    transform: "",
    margin: "0 auto",
    boxSizing: "border-box",
    width: `${renderedWidth}px`,
    maxWidth: "100%",
    height: `${renderedHeight}px`,
    aspectRatio: "",
  });

  html.style.minHeight = `${renderedHeight}px`;
  body.style.minHeight = `${renderedHeight}px`;

  inner.setAttribute("width", String(renderedWidth));
  inner.setAttribute("height", String(renderedHeight));
}

function removeCleonVisualSpacers(reason = "done") {
  try {
    const docs = new Set([document]);

    try {
      const inner = window.frameElement;
      if (inner?.ownerDocument) docs.add(inner.ownerDocument);
    } catch {}

    try {
      const outer = window.parent?.frameElement;
      if (outer?.ownerDocument) docs.add(outer.ownerDocument);
    } catch {}

    docs.forEach((doc) => {
      doc.querySelectorAll?.("[data-cleon-visual-spacer]").forEach((spacer) => {
        spacer.setAttribute("data-cleon-spacer-removed", reason);
        spacer.remove();
      });
    });
  } catch {}
}

export function collapseIframes() {
  const inner = window.frameElement;
  const outer = window.parent?.frameElement;

  try {
    applyStyles(document.documentElement, {
      height: "",
      minHeight: "",
    });

    applyStyles(document.body, {
      height: "",
      minHeight: "",
      margin: "",
    });
  } catch {}

  const collapse = (el, label) => {
    if (!el) {
      return;
    }

    applyStyles(el, {
      width: "1px",
      height: "1px",
      minWidth: "1px",
      minHeight: "1px",
      maxWidth: "1px",
      maxHeight: "1px",
      overflow: "hidden",
      display: "block",
      border: "0",
      position: "static",
    });

    el.setAttribute("width", "1");
    el.setAttribute("height", "1");

  };

  try { collapse(inner, "inner"); } catch (e) {}

  try { collapse(outer, "outer"); } catch (e) {}
}